import { useMount, useStates } from '@tiger/hooks';
import share from '@tiger/share';
import { Button, Empty, Input } from 'antd';
import qrcode from 'qrcode';
import React, { useEffect } from 'react';
import { EasyField, useForm, withForm } from 'react-antd-formutil';
import { ShareAltOutlined } from '@ant-design/icons';
import Card from 'components/Card';
import Pagination from 'components/Pagination';
import Toast from 'components/Toast';
import Track from 'components/Track';
import { statusTextMap } from 'modules/App/Profile/Header';
import APP from 'stores/APP';
import security from 'stores/security';
import { language, printf } from 'utils/i18n';
import URL from 'utils/URL';
import { getInvitationList, resetInvite } from './request';
import './style.scss';

interface InviteState {
    editable: boolean;
    code: string;
    list: Array<{ phone: string; email: string; status: string; signup_at: string }>;
    total: number;
    link: string;
    qrUrl?: string;
}

function inviteLinkByCode(code: string = '') {
    return URL.merge(APP.isDev ? 'https://tradeup-dev.tigerfintech.com' : 'https://www.tradeup.com', {
        pathname: '/accounts/signup',
        query: {
            invite: code || undefined
        }
    });
}

const Invitation: React.FC = () => {
    const $formutil = useForm<{ code: string; page: number; per_page: number }>();
    const currentInviteCode = security.userInfo!.inviteCode;
    const [{ link, editable, code, total, list, qrUrl }, setState] = useStates<InviteState, {}>({
        editable: false,
        code: currentInviteCode!,
        link: inviteLinkByCode(currentInviteCode),
        total: 0,
        list: []
    });

    useMount(() => {
        fetchList();
    });

    useEffect(() => {
        share.update({
            lang: language as any,
            url: link,
            desc: __(
                'Reimagine Investing with TradeUP! Our FinTech platform helps you trade stocks, options and more commission-free with confidence. GET STARTED!'
            ),
            title: __('Invest in Stocks, ETF’s, Options and more with TradeUP!')
        });

        const genQrcode = async () => {
            try {
                const ret = await qrcode.toDataURL(link);

                setState({
                    qrUrl: ret
                });
            } catch (e) {
                Toast.show(e);
            }
        };

        genQrcode();
    }, [link, setState]);

    const fetchList = async () => {
        const { page = 1, per_page = 20 } = $formutil.$params;
        const params = {
            per_page: per_page,
            page
        };

        Toast.loading(true);

        try {
            const { data } = await getInvitationList(params);

            setState({
                list: data.invitees,
                total: data.total
            });
        } catch (err) {
            Toast.show(err);
        }

        Toast.loading(false);
    };

    const onClickEdit = () => {
        if (editable) {
            APP.trackEvent('invitation', 'click', 'modify_code');

            APP.trackEvent('邀请好友', '我的邀请', '保存邀请码');
            Toast.loading(true);

            const curCode = $formutil.$new().$params.code;

            // reset code
            resetInvite(curCode)
                .then(() => {
                    Toast.show(__('邀请码更新成功!'));
                    security.updateInviteCode(curCode);
                    Toast.loading(false);

                    setState({
                        code: curCode,
                        editable: false,
                        link: inviteLinkByCode(curCode)
                    });
                })
                .catch(e => {
                    Toast.loading(false);
                    Toast.show(e.message);
                });
        } else {
            Toast.loading(false);

            // change to edit mode
            setState({
                editable: true,
                code: currentInviteCode
            });

            $formutil.$setValues({ code: currentInviteCode });

            APP.trackEvent('邀请好友', '我的邀请', '修改邀请码');
        }
    };

    return (
        <Card page className="invitation-page">
            <Card form>
                <h2>{__('我的邀请')}</h2>
                <div className="invite-code-wrap">
                    <h3>{__('我的专属邀请链接')}</h3>
                    <section>
                        <label>{__('邀请码')}</label>
                        <div className="field">
                            {editable ? (
                                <EasyField
                                    name="code"
                                    $defaultValue={code}
                                    maxLength={10}
                                    $parser={($viewValue, $setValue) =>
                                        $setValue(
                                            $viewValue
                                                .trim()
                                                .toUpperCase()
                                                .replace(/[^\da-zA-Z]/g, '')
                                        )
                                    }>
                                    {props => <Input {...props} />}
                                </EasyField>
                            ) : (
                                <span>{code || __('尚未设置')}</span>
                            )}
                        </div>
                        <Button
                            type="primary"
                            disabled={editable && $formutil.$params.code === currentInviteCode}
                            onClick={onClickEdit}>
                            {editable ? __('保存') : __('修改')}
                        </Button>
                    </section>
                    {link && (
                        <>
                            <div>
                                <Track eventModel="邀请好友" eventType="我的邀请" eventName="分享邀请链接">
                                    <Button type="link" className="invite-link" onClick={() => share.share()}>
                                        {link}
                                        <ShareAltOutlined className="icon-share" />
                                    </Button>
                                </Track>
                            </div>
                            <div className="qrcode-box">
                                <img alt="qrcode" src={qrUrl} />
                                <p>
                                    {__('二维码邀请')}
                                    <br />
                                    {__('您可以让朋友扫描二维码来邀请ta在TradeUP开户')}
                                </p>
                            </div>
                        </>
                    )}
                </div>
                <h3 className="invite-table-h3">
                    {__('我的邀请记录')}
                    <small>{printf(__('（共邀请好友%s个）'), total)}</small>
                </h3>
                <div className="invite-table-wrap">
                    {total > 0 && (
                        <table className="invite-table">
                            <thead>
                                <tr>
                                    <th>{__('序号')}</th>
                                    <th>{__('手机号')}</th>
                                    <th>{__('邮箱')}</th>
                                    <th>{__('账户状态')}</th>
                                    <th>{__('注册时间')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((item, index) => {
                                    return (
                                        <tr key={`${item.phone}-${item.email}`}>
                                            <td>{index + 1}</td>
                                            <td>{item.phone || '--'}</td>
                                            <td>{item.email || '--'}</td>
                                            <td>{statusTextMap[item.status]}</td>
                                            <td>{item.signup_at}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                {total <= 0 ? (
                    <Empty description={__('你还未邀请好友')} />
                ) : (
                    <Pagination
                        simple
                        total={total}
                        pageName="page"
                        pageSizeName="per_page"
                        defaultPageSize={20}
                        onPageChange={fetchList}
                    />
                )}
            </Card>
        </Card>
    );
};

export default withForm(Invitation);
